import { supabase } from '../supabaseClient';

export const historiqueService = {
  async getHistoriqueData(editionYear) {
    try {
      console.log('Getting historique data for edition:', editionYear);
      
      // Get edition ID and basic info
      const { data: edition } = await supabase
        .from('editions')
        .select('*')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        console.log(`Edition ${editionYear} not found`);
        throw new Error(`Edition ${editionYear} not found`);
      }

      // Get historique-specific data
      const { data: historiqueData } = await supabase
        .from('historique_data')
        .select('*')
        .eq('edition_id', edition.id)
        .single();

      // Get speakers data
      const { data: speakers } = await supabase
        .from('speakers')
        .select('name, title, organization, country, type')
        .eq('edition_id', edition.id)
        .order('display_order');

      // Get topics data
      const { data: topics } = await supabase
        .from('topics')
        .select('title, description')
        .eq('edition_id', edition.id)
        .order('display_order');

      // Get committee members from apropos
      const { data: committee } = await supabase
        .from('apropos_committee_members')
        .select('name, title, organization')
        .eq('edition_id', edition.id)
        .order('display_order');

      // Get conferences count
      const { count: conferencesCount } = await supabase
        .from('conferences')
        .select('*', { count: 'exact', head: true })
        .eq('edition_id', edition.id);

      // Get visitors count
      const { count: visitorsCount } = await supabase
        .from('visiteurs')
        .select('*', { count: 'exact', head: true })
        .eq('edition_id', edition.id);

      // Format the data
      const formattedData = {
        title: `${edition.name} - ${edition.year}`,
        theme: historiqueData?.theme || edition.theme || 'Thème non défini',
        date: `Du ${new Date(edition.start_date).toLocaleDateString()} au ${new Date(edition.end_date).toLocaleDateString()}`,
        lieu: edition.location || 'Lieu non défini',
        participants: historiqueData?.participants_count || `${visitorsCount || 0} participants`,
        resume: historiqueData?.resume || edition.description || 'Résumé non disponible',
        highlights: historiqueData?.highlights ? historiqueData.highlights.split('\n').filter(h => h.trim()) : [],
        stats: {
          conferences: conferencesCount || 0,
          participants: visitorsCount || 0,
          speakers: speakers?.length || 0,
          topics: topics?.length || 0
        },
        speakers: {
          presentiel: (speakers || []).filter(s => s.type === 'regular').map(s => ({
            nom: s.name,
            fonction: s.title,
            organisation: s.organization,
            nationalite: s.country
          })),
          online: (speakers || []).filter(s => s.type === 'online').map(s => ({
            nom: s.name,
            fonction: s.title,
            organisation: s.organization,
            nationalite: s.country
          }))
        },
        topics: (topics || []).map(t => `${t.title}: ${t.description}`),
        comites: (committee || []).map(c => ({
          nom: c.name,
          fonction: c.title,
          organisation: c.organization
        }))
      };

      return formattedData;
    } catch (error) {
      console.error('Error getting historique data:', error);
      throw error;
    }
  },

  async updateHistoriqueData(historiqueData, editionYear) {
    try {
      // Get edition ID
      const { data: edition } = await supabase
        .from('editions')
        .select('id')
        .eq('year', editionYear)
        .single();

      if (!edition) {
        throw new Error(`Edition ${editionYear} not found`);
      }

      const updateData = {
        edition_id: edition.id,
        theme: historiqueData.theme || '',
        participants_count: historiqueData.participants || '',
        resume: historiqueData.resume || '',
        highlights: Array.isArray(historiqueData.highlights) ? 
          historiqueData.highlights.join('\n') : 
          (historiqueData.highlights || ''),
        updated_at: new Date().toISOString()
      };

      // Try to update existing record first
      const { data: existingData } = await supabase
        .from('historique_data')
        .select('id')
        .eq('edition_id', edition.id)
        .single();

      if (existingData) {
        // Update existing record
        const { data, error } = await supabase
          .from('historique_data')
          .update(updateData)
          .eq('id', existingData.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('historique_data')
          .insert([updateData])
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error updating historique data:', error);
      throw error;
    }
  },

  async getAvailableEditions() {
    try {
      // Get all editions except active ones
      const { data: editions } = await supabase
        .from('editions')
        .select('year, name, theme')
        .eq('is_active', false)
        .order('year', { ascending: false });

      return editions || [];
    } catch (error) {
      console.error('Error getting available editions:', error);
      return [];
    }
  }
};
